import React, { useEffect, useState } from "react";
import { Check, Download, Trash2, Film, Instagram, Loader2, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { getAdminContent, updateContentStatus, deleteContentSubmission, contentFileUrl } from "../../lib/api";

const BADGE = {
  submitted: "border-sand text-mocha",
  approved: "border-burgundy text-burgundy",
  posted: "border-espresso bg-espresso text-bone",
};

export function AdminContentPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try { setItems(await getAdminContent()); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    setBusy(id);
    try {
      await updateContentStatus(id, status);
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
      toast.success(status === "posted" ? "Marked as posted." : "Marked as approved.");
    } catch { toast.error("Could not update."); } finally { setBusy(null); }
  };

  const remove = async (id) => {
    setBusy(id);
    try {
      await deleteContentSubmission(id);
      setItems((prev) => prev.filter((it) => it.id !== id));
      toast.success("Removed.");
    } catch { toast.error("Could not remove."); } finally { setBusy(null); }
  };

  if (loading) return <div className="py-16 text-center text-ink/50" data-testid="admin-content-loading"><Loader2 className="animate-spin inline mr-2" size={16} /> Loading content…</div>;
  if (items.length === 0) return <div className="py-16 text-center" data-testid="admin-content-empty"><p className="font-serif text-2xl text-ink">No content submitted yet.</p><p className="mt-2 text-sm text-ink/55">Salons can submit photos & videos from their dashboard.</p></div>;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="admin-content-grid">
      {items.map((item) => (
        <article key={item.id} className="border border-sand/80 bg-bone rounded-lg overflow-hidden flex flex-col" data-testid={`admin-content-${item.id}`}>
          <div className="relative aspect-[4/3] bg-ink/5">
            {item.media_type === "video"
              ? <video src={contentFileUrl(item.storage_path)} className="h-full w-full object-cover" controls data-testid={`admin-content-video-${item.id}`} />
              : <img src={contentFileUrl(item.storage_path)} alt={item.caption || "submission"} className="h-full w-full object-cover" data-testid={`admin-content-image-${item.id}`} />}
            <span className={`absolute left-2 top-2 text-[9px] tracking-[0.12em] uppercase px-2 py-0.5 rounded border ${BADGE[item.status] || BADGE.submitted}`} data-testid={`admin-content-status-${item.id}`}>{item.status}</span>
            {item.media_type === "video" && <Film size={14} className="absolute right-2 top-2 text-bone drop-shadow" />}
          </div>
          <div className="p-4 flex flex-col flex-1">
            <p className="text-sm font-medium text-ink">{item.salon_name}</p>
            <a href={`https://instagram.com/${item.instagram_handle}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-burgundy hover:text-espresso mt-0.5"><Instagram size={12} /> @{item.instagram_handle}</a>
            {item.caption && <p className="mt-2 text-sm text-ink/65 line-clamp-3">{item.caption}</p>}
            <div className="mt-auto pt-4 flex flex-wrap items-center gap-2">
              <a href={contentFileUrl(item.storage_path)} target="_blank" rel="noreferrer" download className="inline-flex items-center gap-1.5 rounded-full border border-sand px-3 py-1.5 text-xs text-ink/70 hover:border-espresso transition-colors" data-testid={`admin-content-download-${item.id}`}><Download size={12} /> Download</a>
              {item.status !== "approved" && item.status !== "posted" && <button type="button" disabled={busy === item.id} onClick={() => setStatus(item.id, "approved")} className="inline-flex items-center gap-1.5 rounded-full border border-burgundy/50 px-3 py-1.5 text-xs text-burgundy hover:bg-burgundy hover:text-bone transition-colors" data-testid={`admin-content-approve-${item.id}`}><Check size={12} /> Approve</button>}
              {item.status !== "posted" && <button type="button" disabled={busy === item.id} onClick={() => setStatus(item.id, "posted")} className="inline-flex items-center gap-1.5 rounded-full border border-espresso/50 px-3 py-1.5 text-xs text-espresso hover:bg-espresso hover:text-bone transition-colors" data-testid={`admin-content-posted-${item.id}`}><CheckCheck size={12} /> Mark posted</button>}
              <button type="button" disabled={busy === item.id} onClick={() => remove(item.id)} className="inline-flex items-center justify-center rounded-full border border-sand p-1.5 text-ink/50 hover:border-burgundy hover:text-burgundy transition-colors ml-auto" data-testid={`admin-content-delete-${item.id}`} aria-label="Remove">{busy === item.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}</button>
            </div>
          </div>
        </article>
      ))}
    </div>
  );
}
